import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { FiEdit2, FiPlus, FiSearch, FiTrash2, FiX, FiMail, FiRefreshCw, FiFileText, FiDownload } from 'react-icons/fi';
import RightWrapper from '../../../Components/CommonComponents/ComponentWrapper';
import ModalPortal, { MODAL_Z } from '../../../Components/CommonComponents/ModalPortal';
import usePincodeLookup from '../../../hooks/usePincodeLookup';
import exportToExcel from '../../../UtilityHookFunctions/exportToExcel';
import {
  BUSINESS_TYPES,
  businessTypeLabel,
  fetchReferralBusinesses,
  createReferralBusiness,
  updateReferralBusiness,
  deleteReferralBusiness,
  fetchLabRequests,
  resendLabRequest,
  cancelLabRequest,
  openLabDocument,
} from '../../../Services/referralBusiness';

const emptyBusiness = {
  name: '',
  type: 'lab',
  email: '',
  ccEmails: '',
  contactPerson: '',
  mobile: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  pincode: '',
  gstNumber: '',
  registrationNo: '',
  commissionPercent: '',
  notes: '',
  autoSendReports: true,
  linkExpiryMinutes: 10080,
};

const EXPIRY_OPTIONS = [
  { value: 1440, label: '24 hours' },
  { value: 4320, label: '3 days' },
  { value: 10080, label: '7 days' },
];

const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-700',
  sent: 'bg-sky-100 text-sky-700',
  uploaded: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-rose-100 text-rose-700',
  cancelled: 'bg-slate-200 text-slate-600',
};

const fmtDate = (value) =>
  value ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const Field = ({ label, children, className = '' }) => (
  <div className={className}>
    <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
    {children}
  </div>
);

const inputClass = 'w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-primary-500 focus:border-primary-500';

const Modal = ({ title, onClose, children, footer, width = 'max-w-3xl' }) => (
  <ModalPortal onClose={onClose}>
    <div className={`fixed inset-0 ${MODAL_Z} flex items-center justify-center overflow-y-auto p-4`}>
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        className={`relative bg-white rounded-2xl shadow-2xl ${width} w-full max-h-[90vh] flex flex-col overflow-hidden`}
      >
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="text-base font-semibold text-gray-800">{title}</h3>
          <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700">
            <FiX className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
        {footer ? <div className="border-t px-5 py-3 flex justify-end gap-2">{footer}</div> : null}
      </div>
    </div>
  </ModalPortal>
);

function BusinessForm({ value, onChange }) {
  const set = (key) => (e) => {
    const next = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    onChange({ ...value, [key]: next });
  };

  // Auto-fill city / state from a 6-digit pincode (ref keeps the latest form values
  // so edits made while the lookup is in flight are not lost).
  const valueRef = useRef(value);
  valueRef.current = value;
  const handlePincodeResult = useCallback(
    (details) => {
      const current = valueRef.current;
      onChange({ ...current, city: details.city || current.city, state: details.state || current.state });
    },
    [onChange]
  );
  const { onPincodeChange, loading: pincodeLoading, error: pincodeError } = usePincodeLookup(handlePincodeResult);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field label="Business name *">
        <input className={inputClass} value={value.name} onChange={set('name')} placeholder="e.g. Sanjeevani Diagnostics" />
      </Field>
      <Field label="Type">
        <select className={inputClass} value={value.type} onChange={set('type')}>
          {BUSINESS_TYPES.map((type) => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </select>
      </Field>

      <Field label="Email (reports are requested here)">
        <input className={inputClass} type="email" value={value.email} onChange={set('email')} placeholder="reports@lab.com" />
      </Field>
      <Field label="CC emails (comma separated)">
        <input className={inputClass} value={value.ccEmails} onChange={set('ccEmails')} placeholder="owner@lab.com, desk@lab.com" />
      </Field>

      <Field label="Contact person">
        <input className={inputClass} value={value.contactPerson} onChange={set('contactPerson')} />
      </Field>
      <Field label="Mobile">
        <input className={inputClass} value={value.mobile} onChange={set('mobile')} />
      </Field>

      <Field label="Landline">
        <input className={inputClass} value={value.phone} onChange={set('phone')} />
      </Field>
      <Field label="City">
        <input className={inputClass} value={value.city} onChange={set('city')} />
      </Field>

      <Field label="Address" className="md:col-span-2">
        <input className={inputClass} value={value.address} onChange={set('address')} />
      </Field>

      <Field label="State">
        <input className={inputClass} value={value.state} onChange={set('state')} />
      </Field>
      <Field label="Pincode">
        <input
          className={inputClass}
          value={value.pincode}
          inputMode="numeric"
          onChange={(e) => {
            set('pincode')(e);
            onPincodeChange(e.target.value);
          }}
        />
        {pincodeLoading ? <p className="mt-1 text-xs text-gray-500">Looking up…</p> : null}
        {!pincodeLoading && pincodeError ? <p className="mt-1 text-xs text-rose-500">{pincodeError}</p> : null}
      </Field>

      <Field label="GST number">
        <input className={inputClass} value={value.gstNumber} onChange={set('gstNumber')} />
      </Field>
      <Field label="Registration number">
        <input className={inputClass} value={value.registrationNo} onChange={set('registrationNo')} />
      </Field>

      <Field label="Commission %">
        <input className={inputClass} type="number" value={value.commissionPercent} onChange={set('commissionPercent')} />
      </Field>
      <Field label="Upload link valid for">
        <select className={inputClass} value={value.linkExpiryMinutes} onChange={set('linkExpiryMinutes')}>
          {EXPIRY_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </Field>

      <Field label="Notes" className="md:col-span-2">
        <textarea rows={2} className={inputClass} value={value.notes} onChange={set('notes')} />
      </Field>

      <label className="md:col-span-2 flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" checked={Boolean(value.autoSendReports)} onChange={set('autoSendReports')} />
        Email an upload link automatically whenever a test is referred here
      </label>
    </div>
  );
}

function RequestsPanel({ business, onClose }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRequests(await fetchLabRequests({ businessId: business._id }));
    } catch (e) {
      toast.error('Could not load the report requests');
    } finally {
      setLoading(false);
    }
  }, [business._id]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, fn, successFallback) => {
    setBusyId(id);
    try {
      const res = await fn(id);
      toast.success(res?.message || successFallback);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Modal title={`Report requests — ${business.name}`} onClose={onClose} width="max-w-4xl">
      {loading ? (
        <p className="text-sm text-gray-500 py-6 text-center">Loading…</p>
      ) : !requests.length ? (
        <p className="text-sm text-gray-500 py-6 text-center">No tests have been referred here yet.</p>
      ) : (
        <div className="space-y-3">
          {requests.map((request) => (
            <div key={request._id} className="border border-gray-200 rounded-xl p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-medium text-gray-800">{request.patientName}</p>
                  <p className="text-xs text-gray-500">Requested {fmtDate(request.createdAt)}</p>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[request.status] || ''}`}>
                  {request.status}
                </span>
              </div>

              <p className="mt-2 text-sm text-gray-600">{(request.tests || []).join(', ')}</p>

              {request.lastError ? <p className="mt-1 text-xs text-rose-600">{request.lastError}</p> : null}

              {(request.documents || []).length ? (
                <div className="mt-3 space-y-1">
                  {request.documents.map((doc) => (
                    <button
                      key={String(doc.documentId)}
                      type="button"
                      onClick={() => openLabDocument(doc.documentId)}
                      className="flex items-center gap-2 text-sm text-primary-600 hover:underline"
                    >
                      <FiFileText className="h-4 w-4" />
                      {doc.filename || 'Report'}
                      <span className="text-xs text-gray-400">{fmtDate(doc.uploadedAt)}</span>
                    </button>
                  ))}
                </div>
              ) : null}

              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  disabled={busyId === request._id}
                  onClick={() => act(request._id, resendLabRequest, 'Upload link sent')}
                  className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1 text-xs hover:bg-gray-50 disabled:opacity-50"
                >
                  <FiRefreshCw className="h-3.5 w-3.5" /> Resend link
                </button>
                {request.status !== 'uploaded' && request.status !== 'cancelled' ? (
                  <button
                    type="button"
                    disabled={busyId === request._id}
                    onClick={() => act(request._id, cancelLabRequest, 'Upload link revoked')}
                    className="inline-flex items-center gap-1 rounded-md border border-rose-200 text-rose-600 px-3 py-1 text-xs hover:bg-rose-50 disabled:opacity-50"
                  >
                    <FiX className="h-3.5 w-3.5" /> Revoke link
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

export default function ReferralBusiness() {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyBusiness);
  const [saving, setSaving] = useState(false);
  const [viewingRequests, setViewingRequests] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await fetchReferralBusinesses({ search, type: typeFilter, limit: 200 });
      setBusinesses(data);
    } catch (e) {
      toast.error('Could not load referral businesses');
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter]);

  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  const openNew = () => {
    setForm(emptyBusiness);
    setEditing('new');
  };

  const openEdit = (business) => {
    setForm({
      ...emptyBusiness,
      ...business,
      ccEmails: (business.ccEmails || []).join(', '),
      commissionPercent: business.commissionPercent ?? '',
      linkExpiryMinutes: business.linkExpiryMinutes || 10080,
    });
    setEditing(business._id);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Business name is required');

    setSaving(true);
    try {
      const payload = { ...form, linkExpiryMinutes: Number(form.linkExpiryMinutes) || 10080 };
      if (editing === 'new') {
        await createReferralBusiness(payload);
        toast.success('Referral business added');
      } else {
        await updateReferralBusiness(editing, payload);
        toast.success('Referral business updated');
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not save the referral business');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (business) => {
    if (!window.confirm(`Remove "${business.name}" from the referral list?`)) return;
    try {
      await deleteReferralBusiness(business._id);
      toast.success('Referral business removed');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not remove the referral business');
    }
  };

  const withoutEmail = useMemo(() => businesses.filter((b) => !b.email).length, [businesses]);

  // Exports the businesses currently listed (respects the search and type filters).
  const handleExport = () => {
    if (!businesses.length) {
      toast.info('No referral businesses to export');
      return;
    }
    exportToExcel(
      businesses,
      `Referral_Businesses_${new Date().toISOString().slice(0, 10)}`,
      [
        { key: 'name', label: 'Business Name' },
        { key: 'type', label: 'Type', format: (v) => businessTypeLabel(v) },
        { key: 'email', label: 'Email', format: (v) => v || '' },
        { key: 'ccEmails', label: 'CC Emails', format: (v) => (Array.isArray(v) ? v.join(', ') : v || '') },
        { key: 'contactPerson', label: 'Contact Person', format: (v) => v || '' },
        { key: 'mobile', label: 'Mobile', format: (v) => v || '' },
        { key: 'phone', label: 'Landline', format: (v) => v || '' },
        { key: 'address', label: 'Address', format: (v) => v || '' },
        { key: 'city', label: 'City', format: (v) => v || '' },
        { key: 'state', label: 'State', format: (v) => v || '' },
        { key: 'pincode', label: 'Pincode', format: (v) => (v ? String(v) : '') },
        { key: 'gstNumber', label: 'GST Number', format: (v) => v || '' },
        { key: 'registrationNo', label: 'Registration No.', format: (v) => v || '' },
        { key: 'commissionPercent', label: 'Commission %', format: (v) => (v === '' || v == null ? '' : Number(v)) },
        { key: 'autoSendReports', label: 'Auto Send Reports', format: (v) => (v ? 'On' : 'Off') },
        { key: 'notes', label: 'Notes', format: (v) => v || '' },
      ],
      'Referral Businesses'
    );
  };

  return (
    <RightWrapper>
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Referral Business</h2>
            <p className="text-xs text-gray-500">
              Labs and centres pathological tests are referred to. Each one is offered on the prescription and
              emailed a one-time link to upload the report.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleExport}
            disabled={loading || !businesses.length}
            className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <FiDownload className="h-4 w-4" /> Export to Excel
          </button>
          <button
            type="button"
            onClick={openNew}
            className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
          >
            <FiPlus className="h-4 w-4" /> Add business
          </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          <div className="relative flex-1 min-w-[220px]">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              className={`${inputClass} pl-9`}
              placeholder="Search by name, email, contact or city"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select className={`${inputClass} w-auto`} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">All types</option>
            {BUSINESS_TYPES.map((type) => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>

        {withoutEmail > 0 ? (
          <p className="mb-3 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
            {withoutEmail} business(es) have no email address, so no upload link can be sent to them automatically.
          </p>
        ) : null}

        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm">
            <thead>
              <tr className="bg-primary-400 text-left text-xs uppercase tracking-wider text-primary-50">
                <th className="px-3 py-2">Business</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Contact</th>
                <th className="px-3 py-2">City</th>
                <th className="px-3 py-2 text-center">Auto send</th>
                <th className="px-3 py-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-500">Loading…</td></tr>
              ) : !businesses.length ? (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-500">No referral businesses yet.</td></tr>
              ) : (
                businesses.map((business) => (
                  <tr key={business._id} className="hover:bg-primary-50">
                    <td className="px-3 py-2 font-medium text-gray-800">{business.name}</td>
                    <td className="px-3 py-2 text-gray-600">{businessTypeLabel(business.type)}</td>
                    <td className="px-3 py-2 text-gray-600">
                      {business.email || <span className="text-rose-500">Not set</span>}
                    </td>
                    <td className="px-3 py-2 text-gray-600">
                      {business.contactPerson}
                      {business.mobile ? <span className="block text-xs text-gray-400">{business.mobile}</span> : null}
                    </td>
                    <td className="px-3 py-2 text-gray-600">{business.city}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${business.autoSendReports ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>
                        {business.autoSendReports ? 'On' : 'Off'}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-center gap-1">
                        <button type="button" onClick={() => setViewingRequests(business)} title="Report requests" className="p-1.5 text-gray-500 hover:text-primary-600">
                          <FiMail className="h-4 w-4" />
                        </button>
                        <button type="button" onClick={() => openEdit(business)} title="Edit" className="p-1.5 text-gray-500 hover:text-primary-600">
                          <FiEdit2 className="h-4 w-4" />
                        </button>
                        <button type="button" onClick={() => remove(business)} title="Remove" className="p-1.5 text-gray-500 hover:text-rose-600">
                          <FiTrash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing ? (
        <Modal
          title={editing === 'new' ? 'Add referral business' : 'Edit referral business'}
          onClose={() => setEditing(null)}
          footer={
            <>
              <button type="button" onClick={() => setEditing(null)} className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50">
                Cancel
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={save}
                className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          }
        >
          <BusinessForm value={form} onChange={setForm} />
        </Modal>
      ) : null}

      {viewingRequests ? (
        <RequestsPanel business={viewingRequests} onClose={() => setViewingRequests(null)} />
      ) : null}
    </RightWrapper>
  );
}
